import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { newsAPI, eventsAPI, galleryAPI, aboutAPI, trainingAPI, testimonialsAPI, partnersAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { 
  Calendar,
  FileText,
  Briefcase,
  Users,
  Camera,
  ArrowRight,
  MapPin,
  Clock,
  Globe,
  User,
  Image as ImageIcon,
  Video,
  Eye,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Quote,
  Building2
} from 'lucide-react';

const LandingContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
    flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 2rem 0;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled(Button)`
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.24);
  }
`;

const HeroLoading = styled.section`
  width: 100%;
  height: 600px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 500px;
  }
`;

const HeroSlider = styled.section`
  position: relative;
  width: 100%;
  height: 600px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 500px;
  }
`;

const SlideContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentSlide * 100}%);
`;

const Slide = styled.div`
  position: relative;
  min-width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
  }
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  padding: 2rem;
  max-width: 900px;
`;

const SlideTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0 0 1rem 0;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
  font-family: ${props => props.theme.typography.fontFamily.heading};
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }
`;

const SlideSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xl};
  margin: 0 0 2rem 0;
  opacity: 0.95;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.8);
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }
`;

const SliderNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;'}
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  color: ${props => props.theme.colors.primary};
  
  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  }
`;

const SliderDots = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: white;
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  background-color: ${props => props.bgSecondary ? props.theme.colors.background.secondary : 'transparent'};
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  text-align: center;
  margin: 0 0 1rem 0;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark || props.theme.colors.primary});
    margin: 1rem auto 3rem;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  text-align: center;
  margin: -2rem auto 3rem;
  max-width: 600px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize.base};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${props => props.theme.colors.primary}30;
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${props => props.theme.colors.primary + '20'};
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  margin: 0 auto 1.5rem auto;
`;

const FeatureTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
`;

const FeatureDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const NewsCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const NewsImage = styled.div`
  width: 100%;
  height: 240px;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.08);
  }
`;

const NewsContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const NewsCategory = styled.span`
  display: inline-block;
  padding: 0.35rem 0.85rem;
  background-color: ${props => {
    const category = props.category?.toLowerCase();
    if (category === 'event') return '#FF6B35';
    if (category === 'announcement') return '#4ECDC4';
    if (category === 'success story') return '#95E1D3';
    if (category === 'press release') return '#F38181';
    return props.theme.colors.primary;
  }};
  color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-start;
`;

const NewsTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const NewsExcerpt = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const ReadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: none;
  padding: 0.75rem 0;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    gap: 0.75rem;
    
    svg {
      transform: translateX(4px);
    }
  }
  
  svg {
    transition: transform 0.3s ease;
  }
`;

const ViewAllNewsButton = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  
  svg {
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    font-size: ${props => props.theme.typography.fontSize.base};
  margin: 0;
  }
`;

const EventCard = styled(Card)`
  padding: 2rem;
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const EventTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const EventDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
`;

const TrainingGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const TrainingCard = styled(Card)`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-top: 4px solid ${props => props.theme.colors.primary};
`;

const TrainingMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const TrainingTitle = styled.h3`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const TrainingDescription = styled.p`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.5;
  flex: 1;
`;

const TrainingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const TrainingStatusTag = styled.span`
  align-self: flex-start;
  padding: 0.25rem 0.65rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props =>
    props.$full ? props.theme.colors.error + '15' : props.theme.colors.success + '15'};
  color: ${props => (props.$full ? props.theme.colors.error : props.theme.colors.success)};
`;

const GalleryCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const GalleryCover = styled.div`
  width: 100%;
  height: 220px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.primary}40);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const GalleryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  padding: 1rem;
  color: white;
  display: flex;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const GalleryContent = styled.div`
  padding: 1.5rem;
`;

const GalleryTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const GalleryDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.6;
`;

// Testimonials Carousel Styles
const TestimonialsCarousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const TestimonialSlide = styled.div`
  text-align: center;
  padding: 2rem;
  opacity: ${props => props.$active ? 1 : 0};
  transform: ${props => props.$active ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.5s ease-in-out;
  position: ${props => props.$active ? 'relative' : 'absolute'};
  width: 100%;
  left: 0;
  top: 0;
`;

const TestimonialQuote = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const TestimonialText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.primary};
  font-style: italic;
  line-height: 1.8;
  margin: 0 0 2rem 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const TestimonialImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TestimonialInfo = styled.div`
  text-align: left;
`;

const TestimonialName = styled.h4`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const TestimonialPosition = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const TestimonialDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const TestimonialDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.primary};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: scale(1.2);
  }
`;

// Partners Carousel Styles
const PartnersCarousel = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 2rem 0;
`;

const PartnersTrack = styled.div`
  display: flex;
  gap: 3rem;
  animation: slide ${props => props.$duration || 30}s linear infinite;
  width: ${props => props.$width || '200%'};
  
  @keyframes slide {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
  
  &:hover {
    animation-play-state: paused;
  }
`;

const PartnerItem = styled.div`
  flex-shrink: 0;
  width: 200px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: grayscale(100%);
    opacity: 0.7;
    transition: all 0.3s ease;
  }
  
  &:hover img {
    filter: grayscale(0%);
    opacity: 1;
  }
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

const LandingPage = () => {
  const { theme } = useTheme();
  const [newsItems, setNewsItems] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [eventsItems, setEventsItems] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [galleryAlbums, setGalleryAlbums] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [trainings, setTrainings] = useState([]);
  const [loadingTrainings, setLoadingTrainings] = useState(true);
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSubtitle, setHeroSubtitle] = useState('Join the Ethiopian Women Human Rights Defenders Network and be part of a powerful movement for change');
  const [heroLoading, setHeroLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  
  // Fetch hero slides and subtitle
  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        setHeroLoading(true);
        const response = await aboutAPI.get();
        if (response.data.data.hero_slides && response.data.data.hero_slides.length > 0) {
          setHeroSlides(response.data.data.hero_slides);
        }
        if (response.data.data.hero_subtitle) {
          setHeroSubtitle(response.data.data.hero_subtitle);
        }
      } catch (error) {
        console.error('Error fetching hero slides:', error);
      } finally {
        setHeroLoading(false);
      }
    };
    
    fetchHeroSlides();
  }, []);

  // Auto-advance slides
  useEffect(() => {
    if (heroSlides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroSlides]);
  
  // Fetch recent news, events, trainings, and gallery from database
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoadingNews(true);
        const response = await newsAPI.getRecent(3); // Get 3 most recent news for landing page
        console.log('Fetched news response:', response);
        // Handle different response structures
        let newsData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            newsData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            newsData = response.data.data;
          } else if (Array.isArray(response.data.items)) {
            newsData = response.data.items;
          }
        }
        console.log('Processed news data:', newsData);
        setNewsItems(newsData);
      } catch (error) {
        console.error('Error fetching news:', error);
        console.error('Error details:', error.response?.data || error.message);
        setNewsItems([]);
      } finally {
        setLoadingNews(false);
      }
    };

    const fetchEvents = async () => {
      try {
        setLoadingEvents(true);
        const response = await eventsAPI.getAll({ per_page: 3 }); // Get 3 most recent events
        console.log('Fetched events response:', response);
        // Handle different response structures
        let eventsData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            eventsData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            eventsData = response.data.data;
          } else if (Array.isArray(response.data.items)) {
            eventsData = response.data.items;
          } else if (response.data.events && Array.isArray(response.data.events)) {
            eventsData = response.data.events;
          }
        }
        // Get only first 3 events
        const recentEvents = Array.isArray(eventsData) ? eventsData.slice(0, 3) : [];
        console.log('Processed events data:', recentEvents);
        setEventsItems(recentEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
        console.error('Error details:', error.response?.data || error.message);
        setEventsItems([]);
      } finally {
        setLoadingEvents(false);
      }
    };

    const fetchGallery = async () => {
      try {
        setLoadingGallery(true);
        const response = await galleryAPI.getFeatured(); // Get featured albums only
        console.log('Fetched gallery response:', response);
        // Handle different response structures
        let galleryData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            galleryData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            galleryData = response.data.data;
          } else if (Array.isArray(response.data.albums)) {
            galleryData = response.data.albums;
          } else if (Array.isArray(response.data.items)) {
            galleryData = response.data.items;
          }
        }
        // Limit to 3 featured albums
        const featuredAlbums = Array.isArray(galleryData) ? galleryData.slice(0, 3) : [];
        console.log('Processed gallery data:', featuredAlbums);
        setGalleryAlbums(featuredAlbums);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Fallback: try to get recent albums if featured fails
        try {
          const fallbackResponse = await galleryAPI.getRecent(3);
          let fallbackData = [];
          if (fallbackResponse && fallbackResponse.data) {
            if (Array.isArray(fallbackResponse.data)) {
              fallbackData = fallbackResponse.data;
            } else if (fallbackResponse.data.data && Array.isArray(fallbackResponse.data.data)) {
              fallbackData = fallbackResponse.data.data;
            }
          }
          setGalleryAlbums(Array.isArray(fallbackData) ? fallbackData.slice(0, 3) : []);
        } catch (fallbackError) {
          console.error('Error fetching recent gallery (fallback):', fallbackError);
        setGalleryAlbums([]);
        }
      } finally {
        setLoadingGallery(false);
      }
    };

    const fetchTrainings = async () => {
      try {
        setLoadingTrainings(true);
        const response = await trainingAPI.getAll({ per_page: 3 });
        let trainingData = [];
        if (response?.data) {
          if (Array.isArray(response.data)) {
            trainingData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            trainingData = response.data.data;
          } else if (Array.isArray(response.data.items)) {
            trainingData = response.data.items;
          }
        }
        setTrainings(trainingData);
      } catch (error) {
        console.error('Error fetching trainings:', error);
        setTrainings([]);
      } finally {
        setLoadingTrainings(false);
      }
    };

    fetchNews();
    fetchEvents();
    fetchGallery();
    fetchTrainings();
    fetchTestimonials();
    fetchPartners();
  }, []);

  // Fetch testimonials
  const fetchTestimonials = async () => {
    try {
      setLoadingTestimonials(true);
      const response = await testimonialsAPI.getAll();
      console.log('Fetched testimonials response:', response);
      // Handle different response structures
      let testimonialsData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          testimonialsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          testimonialsData = response.data.data;
        } else if (Array.isArray(response.data.items)) {
          testimonialsData = response.data.items;
        }
      }
      console.log('Processed testimonials data:', testimonialsData);
      setTestimonials(testimonialsData);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      console.error('Error details:', error.response?.data || error.message);
      setTestimonials([]);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await partnersAPI.getAll();
      console.log('Fetched partners response:', response);
      // Handle different response structures
      let partnersData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          partnersData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          partnersData = response.data.data;
        } else if (Array.isArray(response.data.items)) {
          partnersData = response.data.items;
        }
      }
      console.log('Processed partners data:', partnersData);
      setPartners(partnersData);
    } catch (error) {
      console.error('Error fetching partners:', error);
      console.error('Error details:', error.response?.data || error.message);
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  // Auto-advance testimonials
  useEffect(() => {
    if (testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex(prev => (prev + 1) % testimonials.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [testimonials]);
  
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  const features = [
    {
      icon: Users,
      title: 'Member Management',
      description: 'Comprehensive member registration, profile management, and networking opportunities for women human rights defenders.'
    },
    {
      icon: Calendar,
      title: 'Events & Training',
      description: 'Access to workshops, conferences, training programs, and professional development opportunities.'
    },
    {
      icon: FileText,
      title: 'Research Publications',
      description: 'Download and access research papers, reports, and publications on human rights issues.'
    },
    {
      icon: Briefcase,
      title: 'Career Opportunities',
      description: 'Find job opportunities, tenders, and career development resources in the human rights sector.'
    },
    {
      icon: Camera,
      title: 'Gallery',
      description: 'View photos and videos from events, conferences, and activities organized by EWHRDN.'
    },
    {
      icon: Globe,
      title: 'Network Building',
      description: 'Connect with fellow human rights defenders, share experiences, and build professional networks.'
    }
  ];
  
  return (
    <LandingContainer>
      <PublicHeader theme={theme} />
      
      {/* Hero Section */}
      {heroLoading ? (
        <HeroLoading id="home">
          {/* Loading state - neutral background while images load */}
        </HeroLoading>
      ) : heroSlides.length > 0 ? (
        <HeroSlider id="home">
          <SlideContainer currentSlide={currentSlide}>
            {heroSlides.map((slide, index) => (
              <Slide 
                key={index}
                image={slide.image ? getStorageUrl(slide.image) : 'https://via.placeholder.com/1920x600/e85d04/ffffff?text=Hero+Image'}
              >
                <SlideContent>
                  <SlideTitle>
                    {slide.title || 'Empowering Women, Defending Rights'}
                  </SlideTitle>
                  <SlideSubtitle>
                    {slide.subtitle || heroSubtitle}
                  </SlideSubtitle>
                  <HeroActions>
                    <HeroButton 
                      variant="secondary" 
                      size="large"
                      onClick={() => window.location.href = '/membership-applications'}
                    >
                      Join Our Network
                    </HeroButton>
                    <HeroButton 
                      variant="primary" 
                      size="large"
                      onClick={() => window.location.href = '/about'}
                    >
                      Learn More
                    </HeroButton>
                  </HeroActions>
                </SlideContent>
              </Slide>
            ))}
          </SlideContainer>
          
          {heroSlides.length > 1 && (
            <>
              <SliderNav direction="left" onClick={prevSlide}>
                <ChevronLeft size={24} />
              </SliderNav>
              <SliderNav direction="right" onClick={nextSlide}>
                <ChevronRight size={24} />
              </SliderNav>
              
              <SliderDots>
                {heroSlides.map((_, index) => (
                  <Dot
                    key={index}
                    active={index === currentSlide}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </SliderDots>
            </>
          )}
        </HeroSlider>
      ) : (
        <HeroSection id="home">
          <HeroContent>
            <HeroTitle>
              Empowering Women, Defending Rights
            </HeroTitle>
            <HeroSubtitle>
              {heroSubtitle}
            </HeroSubtitle>
            <HeroActions>
              <HeroButton 
                variant="secondary" 
                size="large"
                onClick={() => window.location.href = '/membership-applications'}
              >
                Join Our Network
              </HeroButton>
              <HeroButton 
                variant="primary" 
                size="large"
                onClick={() => window.location.href = '/about'}
              >
                Learn More
              </HeroButton>
            </HeroActions>
          </HeroContent>
        </HeroSection>
      )}
      
      <Section id="about">
        <SectionTitle>What We Do</SectionTitle>
        <SectionSubtitle>
          Empowering women human rights defenders across Ethiopia through comprehensive support and resources
        </SectionSubtitle>
        <Grid>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <FeatureCard key={index}>
                <FeatureIcon>
                  <Icon size={40} />
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>
                  {feature.description}
                </FeatureDescription>
                <Button 
                  variant="outline" 
                  size="small"
                  onClick={() => {
                    if (feature.title === 'Member Management') {
                      window.location.href = '/members/add';
                    } else if (feature.title === 'Events & Training') {
                      const eventsSection = document.getElementById('events');
                      if (eventsSection) {
                        eventsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    } else if (feature.title === 'Research Publications') {
                      window.location.href = '/publications';
                    } else if (feature.title === 'Career Opportunities') {
                      window.location.href = '/contact';
                    }
                  }}
                >
                  Learn More <ArrowRight size={16} />
                </Button>
              </FeatureCard>
            );
          })}
        </Grid>
      </Section>
      
      <Section id="news" bgSecondary>
        <SectionTitle>Latest News & Updates</SectionTitle>
        <SectionSubtitle>
          Stay informed about our latest activities, events, and success stories
        </SectionSubtitle>
        {loadingNews ? (
          <LoadingState>
            <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Loading latest news...</p>
          </LoadingState>
        ) : newsItems.length === 0 ? (
          <EmptyState>
            <FileText size={48} />
            <p>No news available at the moment.</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Check back soon for updates on our activities and initiatives.
            </p>
          </EmptyState>
        ) : (
          <NewsGrid>
          {newsItems.map((news, index) => (
              <NewsCard 
                key={news.id || index}
              >
              <NewsImage>
                  {news.featured_image_url ? (
                    <img 
                      src={getStorageUrl(news.featured_image_url)} 
                      alt={news.title}
                    />
                  ) : (
                    <Camera size={64} style={{ opacity: 0.2 }} />
                  )}
              </NewsImage>
                
                <NewsContent>
                  {news.category && (
                    <NewsCategory category={news.category}>
                      {news.category}
                    </NewsCategory>
                  )}
                  
              <NewsTitle>{news.title}</NewsTitle>
                  
              <NewsMeta>
                    <span>
                      <Calendar size={12} />
                      {new Date(news.published_date || news.created_at).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span>
                      <User size={12} />
                      {news.author?.full_name || news.author?.fullName || 'EWHRDN'}
                    </span>
              </NewsMeta>
                  
                  <NewsExcerpt>
                    {news.summary || news.excerpt || news.content?.substring(0, 120) + '...'}
                  </NewsExcerpt>
                  
                  <ReadMoreButton onClick={() => window.location.href = `/news/${news.id}`}>
                    Read More <ArrowRight size={16} />
                  </ReadMoreButton>
                </NewsContent>
            </NewsCard>
          ))}
          </NewsGrid>
        )}
        
        <ViewAllNewsButton>
          <Button 
            variant="primary" 
            size="large"
            onClick={() => window.location.href = '/news'}
          >
            View All News <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Button>
        </ViewAllNewsButton>
      </Section>
      
      <Section id="events">
        <SectionTitle>Upcoming Events</SectionTitle>
        <SectionSubtitle>
          Join us in our workshops, conferences, and advocacy programs
        </SectionSubtitle>
        
        {loadingEvents ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            Loading events...
          </p>
        ) : eventsItems.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No upcoming events at the moment. Check back soon!
          </p>
        ) : (
        <Grid>
            {eventsItems.map((event, index) => (
              <EventCard key={event.id || index}>
            <EventHeader>
              <Calendar size={24} style={{ color: '#FF6B35' }} />
                  <EventTitle>{event.title}</EventTitle>
            </EventHeader>
            <EventMeta>
              <MapPin size={16} />
                  <span>{event.location || 'Location TBA'}</span>
              <Clock size={16} />
                  <span>{new Date(event.start_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}</span>
            </EventMeta>
            <EventDescription>
                  {event.description?.substring(0, 120)}...
            </EventDescription>
            <Button 
              variant="primary" 
              size="small"
                  onClick={() => window.location.href = `/events`}
            >
                  Read More <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
            </Button>
          </EventCard>
            ))}
          </Grid>
        )}
        
        <ViewAllNewsButton>
            <Button 
              variant="primary" 
            size="large"
            onClick={() => window.location.href = '/events'}
          >
            View All Events <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
            </Button>
        </ViewAllNewsButton>
      </Section>

      <Section id="training" bgSecondary>
        <SectionTitle>Training Opportunities</SectionTitle>
        <SectionSubtitle>
          Apply for upcoming capacity-building programs curated for women human rights defenders
        </SectionSubtitle>

        {loadingTrainings ? (
          <LoadingState>
            <GraduationCap size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Loading trainings...</p>
          </LoadingState>
        ) : trainings.length === 0 ? (
          <EmptyState>
            <GraduationCap size={48} />
            <p>No trainings are open for application right now.</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Check back soon or follow our social channels for announcements.
            </p>
          </EmptyState>
        ) : (
          <TrainingGrid>
            {trainings.map((training, index) => {
              const seatsLeft = typeof training.remaining_seats === 'number'
                ? Math.max(training.remaining_seats, 0)
                : null;
              const isFull = training.is_full || (seatsLeft !== null && seatsLeft <= 0);

              return (
                <TrainingCard key={training.id || index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <TrainingTitle>{training.title}</TrainingTitle>
                    {training.max_participants && (
                      <TrainingStatusTag theme={theme} $full={isFull}>
                        {isFull
                          ? 'Registration Closed'
                          : `${seatsLeft ?? training.max_participants} seats left`}
                      </TrainingStatusTag>
                    )}
                  </div>
                  <TrainingMeta>
                  <span>
                    <Calendar size={14} />
                    {training.start_date
                      ? new Date(training.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Date TBA'}
                  </span>
                  {training.location && (
                    <span>
                      <MapPin size={14} />
                      {training.location}
                    </span>
                  )}
                  {training.registration_deadline && (
                    <span>
                      <Clock size={14} />
                      Apply by {new Date(training.registration_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                    {training.max_participants && (
                      <span>
                        <Users size={14} />
                        {seatsLeft !== null
                          ? `${seatsLeft} of ${training.max_participants} seats left`
                          : `Max ${training.max_participants} participants`}
                      </span>
                    )}
                  </TrainingMeta>
                  <TrainingDescription>
                    {training.summary || training.description?.substring(0, 140) + '...'}
                  </TrainingDescription>
                  <TrainingActions>
                    <Button
                      variant="primary"
                      disabled={isFull}
                      onClick={() => {
                        if (!isFull) {
                          window.location.href = `/training/${training.id}/register`;
                        }
                      }}
                    >
                      {isFull ? 'Registration Closed' : 'Apply Now'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = `/training?highlight=${training.id}`}
                    >
                      View Details
                    </Button>
                  </TrainingActions>
                </TrainingCard>
              );
            })}
          </TrainingGrid>
        )}
      </Section>
      
      <Section id="gallery" bgSecondary>
        <SectionTitle>Photo & Video Gallery</SectionTitle>
        <SectionSubtitle>
          Explore our collection of memories from events, conferences, and community activities
        </SectionSubtitle>
        
        {loadingGallery ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            Loading gallery...
          </p>
        ) : galleryAlbums.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No albums available yet. Check back soon!
          </p>
        ) : (
          <Grid>
            {galleryAlbums.map((album, index) => (
              <GalleryCard key={album.id || index}>
                <GalleryCover onClick={() => window.location.href = `/gallery#album-${album.id}`}>
                  {album.cover_image ? (
                    <img src={getStorageUrl(album.cover_image)} alt={album.title} />
                  ) : (
                    <Camera size={64} />
                  )}
                  <GalleryOverlay>
                    <span>
                      <ImageIcon size={16} /> {album.images_count || 0}
                    </span>
                    <span>
                      <Video size={16} /> {album.videos_count || 0}
                    </span>
                  </GalleryOverlay>
                </GalleryCover>
                
                <GalleryContent>
                  <GalleryTitle>{album.title}</GalleryTitle>
                  <GalleryDescription>
                    {album.description || 'Click to view photos and videos from this album'}
                  </GalleryDescription>
                  <Button 
                    variant="primary" 
                    size="small"
                    onClick={() => window.location.href = `/gallery#album-${album.id}`}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    <Eye size={16} style={{ marginRight: '0.5rem' }} /> View Album
                  </Button>
                </GalleryContent>
              </GalleryCard>
            ))}
          </Grid>
        )}
        
        <ViewAllNewsButton>
          <Button 
            variant="primary" 
            size="large"
            onClick={() => window.location.href = '/gallery'}
          >
            View Full Gallery <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Button>
        </ViewAllNewsButton>
      </Section>

      {/* Testimonials Section */}
      <Section id="testimonials">
        <SectionTitle>What People Say</SectionTitle>
        <SectionSubtitle>
          Hear from our members and partners about their experience with EWHRDN
        </SectionSubtitle>
        
        {loadingTestimonials ? (
          <LoadingState>
            <Quote size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Loading testimonials...</p>
          </LoadingState>
        ) : testimonials.length === 0 ? (
          <EmptyState>
            <Quote size={48} />
            <p>No testimonials available at the moment.</p>
          </EmptyState>
        ) : (
          <TestimonialsCarousel>
            {testimonials.map((testimonial, index) => (
              <TestimonialSlide key={testimonial.id} $active={index === currentTestimonialIndex}>
                <TestimonialQuote>
                  <Quote size={48} />
                </TestimonialQuote>
                <TestimonialText>{testimonial.testimonial}</TestimonialText>
                <TestimonialAuthor>
                  {testimonial.image_url && (
                    <TestimonialImage>
                      <img src={getStorageUrl(testimonial.image_url)} alt={testimonial.name} />
                    </TestimonialImage>
                  )}
                  <TestimonialInfo>
                    <TestimonialName>{testimonial.name}</TestimonialName>
                    <TestimonialPosition>
                      {testimonial.position}
                      {testimonial.organization && ` at ${testimonial.organization}`}
                    </TestimonialPosition>
                  </TestimonialInfo>
                </TestimonialAuthor>
              </TestimonialSlide>
            ))}
            {testimonials.length > 1 && (
              <TestimonialDots>
                {testimonials.map((_, index) => (
                  <TestimonialDot
                    key={index}
                    $active={index === currentTestimonialIndex}
                    onClick={() => setCurrentTestimonialIndex(index)}
                  />
                ))}
              </TestimonialDots>
            )}
          </TestimonialsCarousel>
        )}
      </Section>

      {/* Partners Section */}
      <Section id="partners" bgSecondary>
        <SectionTitle>Our Partners</SectionTitle>
        <SectionSubtitle>
          We work with organizations and institutions committed to advancing women's human rights
        </SectionSubtitle>
        
        {loadingPartners ? (
          <LoadingState>
            <Building2 size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Loading partners...</p>
          </LoadingState>
        ) : partners.length === 0 ? (
          <EmptyState>
            <Building2 size={48} />
            <p>No partners to display at the moment.</p>
          </EmptyState>
        ) : partners.length > 0 ? (
          <PartnersCarousel>
            <PartnersTrack $width={`${partners.length * 250}px`} $duration={partners.length * 3}>
              {/* Duplicate partners for seamless loop */}
              {[...partners, ...partners].map((partner, index) => (
                <PartnerItem key={`${partner.id}-${index}`}>
                  {partner.website_url ? (
                    <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                      {partner.logo_url ? (
                        <img src={getStorageUrl(partner.logo_url)} alt={partner.name} />
                      ) : (
                        <span style={{ fontSize: '1rem', color: '#666' }}>{partner.name}</span>
                      )}
                    </a>
                  ) : (
                    <>
                      {partner.logo_url ? (
                        <img src={getStorageUrl(partner.logo_url)} alt={partner.name} />
                      ) : (
                        <span style={{ fontSize: '1rem', color: '#666' }}>{partner.name}</span>
                      )}
                    </>
                  )}
                </PartnerItem>
              ))}
            </PartnersTrack>
          </PartnersCarousel>
        ) : null}
      </Section>
      
      <PublicFooter theme={theme} />
    </LandingContainer>
  );
};

export default LandingPage;
